<template>
  <div class="order-list page-enter-fade-up">
    <!-- 工具栏 -->
    <BaseCard class="toolbar-card">
      <div class="filters filter-section">
        <!-- 日期筛选 -->
        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          @change="handleFilterChange"
          class="date-picker"
        />

        <BaseButton
          variant="outline"
          size="small"
          class="my-today-btn"
          :class="{ 'is-active': isMyTodayFilter }"
          @click="applyMyToday"
        >
          我的今日
        </BaseButton>

        <BaseButton
          v-if="canAccessReconciliation"
          variant="primary"
          size="small"
          @click="router.push('/main/payment-reconciliation')"
        >
          当日对账
        </BaseButton>

        <!-- 订单类型筛选 -->
        <el-select
          v-model="filters.orderType"
          placeholder="订单类型"
          multiple
          clearable
          collapse-tags
          collapse-tags-tooltip
          @change="handleFilterChange"
          class="filter-select filter-select--type"
        >
          <el-option
            v-for="type in orderTypes"
            :key="type"
            :label="type"
            :value="type"
          />
        </el-select>

        <!-- 经办人筛选 -->
        <el-select
          v-model="filters.handler"
          placeholder="经办人"
          multiple
          clearable
          filterable
          collapse-tags
          collapse-tags-tooltip
          @change="handleFilterChange"
          class="filter-select filter-select--handler"
        >
          <el-option
            v-for="h in handlers"
            :key="h._id"
            :label="h.username"
            :value="h._id"
          />
        </el-select>

        <!-- 支付方式筛选 -->
        <el-select
          v-model="filters.paymentMethod"
          placeholder="支付方式"
          multiple
          clearable
          collapse-tags
          collapse-tags-tooltip
          @change="handleFilterChange"
          class="filter-select filter-select--payment"
        >
          <el-option
            v-for="opt in paymentMethodOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>

        <BaseButton
          v-if="selectedOrders.length > 0"
          variant="primary"
          size="small"
          @click="handleViewSelectedOrders"
        >
          查看凭证（{{ selectedOrders.length }}）
        </BaseButton>

        <div class="flex-spacer desktop-only"></div>
        <BaseInput
          v-model="filters.search"
          placeholder="搜索订单号/学员姓名"
          clearable
          @input="handleSearch"
          class="search-input"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </BaseInput>
      </div>
    </BaseCard>

    <!-- 订单列表 -->
    <BaseCard class="table-card">
      <div v-if="orderSummary" class="order-summary-bar">
        <span class="order-summary-bar__item">
          <span class="order-summary-bar__label">订单笔数</span>
          <span class="order-summary-bar__value">{{ orderSummary.totalOrders }}</span>
        </span>
        <span class="order-summary-bar__sep" aria-hidden="true">·</span>
        <span class="order-summary-bar__item">
          <span class="order-summary-bar__label">报名</span>
          <span class="order-summary-bar__value">{{ orderSummary.enrollmentCount }} 笔</span>
        </span>
        <span class="order-summary-bar__sep" aria-hidden="true">·</span>
        <span class="order-summary-bar__item">
          <span class="order-summary-bar__label">补费</span>
          <span class="order-summary-bar__value">{{ orderSummary.supplementCount }} 笔</span>
        </span>
        <span class="order-summary-bar__sep" aria-hidden="true">·</span>
        <span class="order-summary-bar__item">
          <span class="order-summary-bar__label">退费</span>
          <span class="order-summary-bar__value">{{ orderSummary.refundCount }} 笔</span>
        </span>
        <span class="order-summary-bar__sep" aria-hidden="true">·</span>
        <span class="order-summary-bar__item">
          <span class="order-summary-bar__label">收费</span>
          <span class="order-summary-bar__value order-summary-bar__value--money">¥{{ formatMoney(orderSummary.chargeActualTotal) }}</span>
        </span>
        <span class="order-summary-bar__sep" aria-hidden="true">·</span>
        <span class="order-summary-bar__item">
          <span class="order-summary-bar__label">退费</span>
          <span class="order-summary-bar__value order-summary-bar__value--money">¥{{ formatMoney(orderSummary.refundActualTotal) }}</span>
        </span>
        <span class="order-summary-bar__sep" aria-hidden="true">·</span>
        <span class="order-summary-bar__item">
          <span class="order-summary-bar__label">实收金额</span>
          <span
            class="order-summary-bar__value"
            :class="(orderSummary.chargeActualTotal - orderSummary.refundActualTotal) >= 0 ? 'order-summary-bar__value--positive' : 'order-summary-bar__value--negative'"
          >¥{{ formatMoney(orderSummary.chargeActualTotal - orderSummary.refundActualTotal) }}</span>
        </span>
        <span class="order-summary-bar__hint">与当前筛选条件一致</span>
      </div>

      <ResponsiveTable
        :data="orders"
        :fields="orderMobileFields"
        row-key="_id"
        :loading="loading"
        style="width: 100%"
        border empty-text="暂无订单"
        @selection-change="handleSelectionChange"
      >
        <el-table-column
          type="selection"
          width="40"
          align="center"
        />
        <el-table-column
          prop="orderNo"
          label="订单号"
          width="150"
          align="center"
        />
        <el-table-column
          label="学员"
          width="100"
          align="center"
        >
          <template #default="{ row }">
            <el-link
              type="primary"
              :underline="false"
              @click="router.push(`/main/student/${row.student?._id}`)"
            >
              {{ row.student?.name }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column
          label="班级信息"
          min-width="200"
          align="center"
        >
          <template #default="{ row }">
            <div>{{ row.class?.course?.name || row.class?.name }}</div>
            <div class="text-gray">
              {{ row.class?.teacher?.name }}/{{ row.class?.classroom?.name }}
            </div>
          </template>
        </el-table-column>
        <el-table-column
          label="订单金额"
          width="160"
          align="center"
        >
          <template #default="{ row }">
            <div>{{ row.orderType === '退费' ? '应退' : '应收' }}：¥{{ row.totalAmount.toFixed(2) }}</div>
            <div v-if="row.transferCredit > 0">转课抵扣：¥{{ row.transferCredit.toFixed(2) }}</div>
            <div>{{ row.orderType === '退费' ? '实退' : '实收' }}：¥{{ row.actualAmount.toFixed(2) }}</div>
          </template>
        </el-table-column>
        <el-table-column
          label="支付方式"
          width="100"
          align="center"
        >
          <template #default="{ row }">
            {{ getPaymentMethodLabel(row.paymentMethod) }}
          </template>
        </el-table-column>
        <el-table-column
          label="订单类型"
          width="100"
          align="center"
        >
          <template #default="{ row }">
            <el-tag :type="getOrderTypeTag(row.orderType)" size="small" effect="plain">
              {{ row.orderType }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="状态"
          width="100"
          align="center"
        >
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)">
              {{ getStatusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="经办人"
          width="100"
          align="center"
        >
          <template #default="{ row }">
            {{ row.handler?.username }}
          </template>
        </el-table-column>
        <el-table-column
          label="申请人"
          width="100"
          align="center"
        >
          <template #default="{ row }">
            {{ row.orderType === '退费' ? (row.applicant?.name || row.applicant?.username || '—') : '—' }}
          </template>
        </el-table-column>
        <el-table-column
          label="创建时间"
          width="160"
          align="center"
        >
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="120"
          fixed="right"
          align="center"
        >
          <template #default="{ row }">
            <el-dropdown trigger="hover">
              <BaseButton variant="text" size="small">
                操作
                <el-icon class="el-icon--right">
                  <arrow-down />
                </el-icon>
              </BaseButton>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item @click="handleViewOrder(row)">
                    <el-icon><View /></el-icon>查看
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="canEditPaymentMethod && !row.courseTransfer"
                    @click="handleEditPaymentMethod(row)"
                    :disabled="row.status === 'voided'"
                  >
                    <el-icon><Edit /></el-icon>修改支付方式
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="canOperateOrders && !row.courseTransfer"
                    @click="handleVoidOrder(row)"
                    :disabled="row.status === 'voided'"
                  >
                    <el-icon><Delete /></el-icon>作废
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </el-table-column>

        <template #cell-orderNo="{ row }">
          <div class="m-order-no">{{ row.orderNo }}</div>
          <el-tag size="small" :type="getStatusType(row.status)">
            {{ getStatusLabel(row.status) }}
          </el-tag>
        </template>
        <template #cell-student="{ row }">
          <el-link
            v-if="row.student?._id"
            type="primary"
            :underline="false"
            @click="router.push(`/main/student/${row.student._id}`)"
          >
            {{ row.student?.name }}
          </el-link>
          <span v-else>—</span>
        </template>
        <template #cell-classInfo="{ row }">
          <div>{{ row.class?.name || '—' }}</div>
          <div v-if="row.class?.teacher?.name || row.class?.classroom?.name" class="text-gray m-order-class-sub">
            {{ row.class?.teacher?.name }}/{{ row.class?.classroom?.name }}
          </div>
        </template>
        <template #cell-amount="{ row }">
          <div>{{ row.orderType === '退费' ? '应退' : '应收' }}：¥{{ row.totalAmount.toFixed(2) }}</div>
          <div v-if="row.transferCredit > 0">转课抵扣：¥{{ row.transferCredit.toFixed(2) }}</div>
          <div>{{ row.orderType === '退费' ? '实退' : '实收' }}：¥{{ row.actualAmount.toFixed(2) }}</div>
        </template>
        <template #cell-orderType="{ row }">
          <el-tag :type="getOrderTypeTag(row.orderType)" size="small" effect="plain">
            {{ row.orderType }}
          </el-tag>
        </template>
        <template #actions="{ row }">
          <BaseButton variant="text" size="small" title="查看凭证" @click="handleViewOrder(row)">
查看
          </BaseButton>
          <BaseButton
            v-if="canEditPaymentMethod && !row.courseTransfer"
            variant="text"
            size="small"
            title="修改支付方式"
            :disabled="row.status === 'voided'"
            @click="handleEditPaymentMethod(row)"
          >
            改支付方式
          </BaseButton>
          <BaseButton
            v-if="canOperateOrders && !row.courseTransfer"
            variant="text"
            size="small"
            title="作废订单"
            :disabled="row.status === 'voided'"
            @click="handleVoidOrder(row)"
          >
            作废
          </BaseButton>
        </template>
      </ResponsiveTable>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="limit"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </BaseCard>

    <!-- 添加凭证对话框 -->
    <el-dialog
      v-model="showVoucher"
      title="业务凭证"
      width="80%"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <enrollment-voucher
        ref="voucherRef"
        :orderIds="currentOrderIds"
      />
    </el-dialog>

    <!-- 修改支付方式对话框 -->
    <el-dialog
      v-model="paymentDialog.visible"
      title="修改支付方式"
      width="420px"
      :close-on-click-modal="false"
    >
      <div class="payment-edit-info">
        <div><span class="payment-edit-info__label">订单编号：</span>{{ paymentDialog.orderNo }}</div>
        <div><span class="payment-edit-info__label">原支付方式：</span>{{ getPaymentMethodLabel(paymentDialog.originalMethod) }}</div>
      </div>
      <el-form label-width="100px" style="margin-top: 16px;">
        <el-form-item label="新支付方式">
          <el-select v-model="paymentDialog.newMethod" placeholder="请选择支付方式" style="width: 100%">
            <el-option
              v-for="opt in paymentMethodOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <BaseButton @click="paymentDialog.visible = false">取消</BaseButton>
        <BaseButton
          variant="primary"
          :loading="paymentDialog.submitting"
          :disabled="!paymentDialog.newMethod || paymentDialog.newMethod === paymentDialog.originalMethod"
          @click="confirmEditPaymentMethod"
        >
          确定
        </BaseButton>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onActivated, computed } from 'vue'
import { useRouter } from 'vue-router'
import { Search, View, Delete, Edit, ArrowDown } from '@element-plus/icons-vue'
import { getOrders, getOrdersSummary, voidOrder, updateOrderPaymentMethod } from '@/api/order'
import { getOrderHandlers } from '@/api/user'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getMergedPermissionsFromLocalStorage, hasModulePermission } from '@/utils/permissions'
import EnrollmentVoucher from '@/components/EnrollmentVoucher.vue'
import ResponsiveTable from '@/components/ResponsiveTable.vue'

const router = useRouter()

const orderMobileFields = [
  { prop: 'orderNo', label: '订单号', primary: true },
  { prop: 'student', label: '学员' },
  { prop: 'classInfo', label: '班级' },
  { prop: 'amount', label: '金额' },
  { prop: 'orderType', label: '类型' },
  { prop: 'paymentMethod', label: '支付方式', hideOnMobile: true },
  { prop: 'status', label: '状态', hideOnMobile: true },
  { prop: 'handler', label: '经办人', hideOnMobile: true },
  { prop: 'applicant', label: '申请人', hideOnMobile: true },
  { prop: 'createdAt', label: '创建时间', hideOnMobile: true }
]

// 状态变量
const loading = ref(false)
const orders = ref([])
const page = ref(1)
const limit = ref(10)
const total = ref(0)

const defaultSummary = () => ({
  totalOrders: 0,
  enrollmentCount: 0,
  supplementCount: 0,
  refundCount: 0,
  chargeActualTotal: 0,
  refundActualTotal: 0
})
const orderSummary = ref(defaultSummary())

// 筛选条件
const filters = ref({
  dateRange: [],
  handler: [],
  orderType: [],
  paymentMethod: [],
  search: ''
})

// 静态数据
const handlers = ref([])
const orderTypes = ['报名', '补费', '退费']

// 支付方式映射
const paymentMethods = {
  wechat: '微信支付',
  alipay: '支付宝',
  cash: '现金',
  bank_transfer: '银行转账',
  other: '其他'
}

const paymentMethodOptions = Object.entries(paymentMethods).map(([value, label]) => ({
  value,
  label
}))

// 凭证相关
const showVoucher = ref(false)
const voucherRef = ref(null)
const currentOrderIds = ref([])

// 多选相关
const selectedOrders = ref([])
const handleSelectionChange = (rows) => {
  selectedOrders.value = rows
}
const userRole = computed(() => localStorage.getItem('role'))
const userPermissions = computed(() => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    return user.permissions || {}
  } catch (error) {
    return {}
  }
})
const hasPermission = (moduleName, action) => {
  if (userRole.value === 'superadmin') return true
  const modulePermissions = userPermissions.value?.[moduleName] || []
  return modulePermissions.includes('*') || modulePermissions.includes(action)
}
const canOperateOrders = computed(() => hasPermission('orders', 'operate'))
const canEditPaymentMethod = computed(() => hasPermission('orders', 'edit_payment_method'))
const canAccessReconciliation = computed(() => {
  const merged = getMergedPermissionsFromLocalStorage()
  return ['view', 'submit', 'review'].some(action =>
    hasModulePermission(userRole.value, merged, 'reconciliations', action)
  )
})

const currentUserId = computed(() => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    return user._id || user.id || ''
  } catch {
    return ''
  }
})

function formatLocalYmd(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const isMyTodayFilter = computed(() => {
  const h = filters.value.handler
  if (!currentUserId.value || !Array.isArray(h) || h.length !== 1 || h[0] !== currentUserId.value) return false
  const dr = filters.value.dateRange
  if (!dr || dr.length !== 2) return false
  const today = formatLocalYmd(new Date())
  return formatLocalYmd(dr[0]) === today && formatLocalYmd(dr[1]) === today
})

const formatMoney = (n) => {
  const x = Number(n)
  if (Number.isNaN(x)) return '0.00'
  return x.toFixed(2)
}

/** 与列表接口相同的筛选字段；includePagination 为 false 时用于汇总接口 */
const buildQueryObject = (includePagination = true) => {
  const o = {}

  if (filters.value.dateRange?.length === 2) {
    o.startDate = formatLocalYmd(filters.value.dateRange[0])
    o.endDate = formatLocalYmd(filters.value.dateRange[1])
  }

  if (filters.value.handler?.length) {
    o.handlerId = filters.value.handler.join(',')
  }

  if (filters.value.orderType?.length) {
    o.orderType = filters.value.orderType.join(',')
  }

  if (filters.value.paymentMethod?.length) {
    o.paymentMethod = filters.value.paymentMethod.join(',')
  }

  if (filters.value.search) {
    if (/^\d+$/.test(filters.value.search)) {
      o.orderNo = filters.value.search
    } else {
      o.studentName = filters.value.search
    }
  }

  if (includePagination) {
    o.page = page.value
    o.limit = limit.value
  }

  return o
}

// 获取订单列表与汇总
const fetchOrders = async () => {
  loading.value = true
  try {
    const listParams = buildQueryObject(true)
    const summaryParams = buildQueryObject(false)
    console.log('查询参数:', listParams)

    const [listRes, sumRes] = await Promise.all([
      getOrders(listParams),
      getOrdersSummary(summaryParams)
    ])

    console.log('订单列表参数:', listParams)
    console.log('订单汇总参数:', summaryParams)
    console.log('订单数据:', listRes)
    console.log('订单汇总原始响应:', sumRes)

    if (listRes.data?.orders) {
      orders.value = listRes.data.orders
      total.value = listRes.data.pagination?.total || 0
    } else if (Array.isArray(listRes.data)) {
      orders.value = listRes.data
      total.value = listRes.pagination?.total ?? listRes.data.length
    } else {
      throw new Error('无效的响应数据格式')
    }

    if (sumRes?.data && typeof sumRes.data === 'object') {
      orderSummary.value = { ...defaultSummary(), ...sumRes.data }
    } else {
      orderSummary.value = defaultSummary()
    }
  } catch (error) {
    console.error('获取订单列表失败:', error)
    ElMessage.error(error.message || '获取订单列表失败')
    orderSummary.value = defaultSummary()
  } finally {
    loading.value = false
  }
}

const applyMyToday = () => {
  const uid = currentUserId.value
  if (!uid) {
    ElMessage.warning('无法获取当前登录用户')
    return
  }
  if (isMyTodayFilter.value) {
    filters.value.dateRange = []
    filters.value.handler = []
    page.value = 1
    fetchOrders()
    return
  }
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  filters.value.dateRange = [start, end]
  filters.value.handler = [uid]
  page.value = 1
  fetchOrders()
}

// 处理筛选变化
const handleFilterChange = () => {
  page.value = 1
  fetchOrders()
}

// 处理搜索
const handleSearch = () => {
  page.value = 1
  fetchOrders()
}

// 处理分页
const handleSizeChange = (val) => {
  limit.value = val
  fetchOrders()
}

const handlePageChange = (val) => {
  page.value = val
  fetchOrders()
}

// 格式化日期
const formatDate = (date) => {
  return new Date(date).toLocaleString()
}

// 获取支付方式标签
const getPaymentMethodLabel = (method) => {
  return paymentMethods[method] || method
}

// 查看订单（单行）
const handleViewOrder = (row) => {
  currentOrderIds.value = [row._id]
  showVoucher.value = true
}

// 查看多选订单凭证
const handleViewSelectedOrders = () => {
  if (selectedOrders.value.length === 0) return
  currentOrderIds.value = selectedOrders.value.map(o => o._id)
  showVoucher.value = true
}

// 修改支付方式
const paymentDialog = ref({
  visible: false,
  submitting: false,
  orderId: '',
  orderNo: '',
  originalMethod: '',
  newMethod: ''
})

const handleEditPaymentMethod = (row) => {
  if (!canEditPaymentMethod.value) {
    ElMessage.warning('没有修改支付方式的权限')
    return
  }
  if (row.status === 'voided') {
    ElMessage.warning('已作废订单不能修改支付方式')
    return
  }
  paymentDialog.value = {
    visible: true,
    submitting: false,
    orderId: row._id,
    orderNo: row.orderNo,
    originalMethod: row.paymentMethod,
    newMethod: row.paymentMethod
  }
}

const confirmEditPaymentMethod = async () => {
  const { orderId, newMethod, originalMethod } = paymentDialog.value
  if (!orderId || !newMethod || newMethod === originalMethod) return
  paymentDialog.value.submitting = true
  try {
    await updateOrderPaymentMethod(orderId, newMethod)
    ElMessage.success('支付方式已更新')
    paymentDialog.value.visible = false
    fetchOrders()
  } catch (error) {
    console.error('修改支付方式失败:', error)
    ElMessage.error(error.message || '修改支付方式失败')
  } finally {
    paymentDialog.value.submitting = false
  }
}

// 经办人下拉（专用接口，不走仅管理员可用的 GET /users）
const loadUsers = async () => {
  try {
    const response = await getOrderHandlers()
    if (response.status === 'success' && Array.isArray(response.data?.users)) {
      handlers.value = response.data.users
    } else {
      handlers.value = []
    }
  } catch (error) {
    console.error('获取经办人列表失败:', error)
    handlers.value = []
  }
}

// 在 script setup 部分添加以下函数
const getStatusLabel = (status) => {
  const labels = {
    pending: '待支付',
    paid: '已支付',
    refunded: '已退费',
    cancelled: '已取消',
    voided: '已作废',
    completed: '已完成'
  }
  return labels[status] || status
}

const getStatusType = (status) => {
  const types = {
    pending: 'warning',
    paid: 'success',
    refunded: 'info',
    cancelled: 'danger',
    voided: 'info'
  }
  return types[status] || ''
}

// 获取订单类型标签样式的函数
const getOrderTypeTag = (type) => {
  const types = {
    '报名': 'success',
    '补费': 'warning',
    '退费': 'danger'
  }
  return types[type] || 'info'
}

// 修改作废处理函数
const handleVoidOrder = async (row) => {
  if (!canOperateOrders.value) {
    ElMessage.warning('没有订单操作权限')
    return
  }
  try {
    await ElMessageBox.confirm(
      '确定要作废该订单吗？作废后无法恢复',
      '作废订单',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    const reason = await ElMessageBox.prompt('请输入作废原因', '作废原因', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputPattern: /\S+/,
      inputErrorMessage: '作废原因不能为空'
    })
    
    loading.value = true
    await voidOrder(row._id, reason.value)
    ElMessage.success('订单已作废')
    fetchOrders() // 刷新列表
  } catch (error) {
    if (error !== 'cancel') {
      console.error('作废订单失败:', error)
      ElMessage.error(error.message || '作废订单失败')
    }
  } finally {
    loading.value = false
  }
}

// 初始化
onMounted(() => {
  fetchOrders()
  loadUsers()
})

let hasActivatedOnce = false
onActivated(() => {
  if (!hasActivatedOnce) {
    hasActivatedOnce = true
    return
  }
  fetchOrders()
  loadUsers()
})
</script>

<style lang="scss" scoped>
@import '@/styles/page-enter.scss';
@import '@/styles/mixins.scss';

.m-order-no {
  font-weight: 600;
  font-size: 13px;
  word-break: break-all;
}

.m-order-class-sub {
  margin-top: 4px;
}

.order-list {
  padding: var(--space-5);
  background: var(--color-bg);
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  line-height: var(--line-height-body);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);

  .toolbar-card,
  .table-card {
    :deep(.base-card__body) {
      padding: 0;
    }
  }

  .toolbar-card {
    :deep(.base-card__body) {
      padding: var(--space-4);
    }
  }

  .table-card {
    :deep(.base-card__body) {
      padding: var(--space-4);
    }
  }

  .order-summary-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2) var(--space-3);
    padding: var(--space-3) var(--space-4);
    margin: 0 var(--space-4) var(--space-3);
    margin-top: var(--space-3);
    background: var(--color-surface-muted);
    border-radius: var(--radius-md);
    font-size: var(--font-size-body-sm);
    color: var(--color-text-secondary);
    line-height: var(--line-height-body-sm);

    &__item {
      display: inline-flex;
      align-items: baseline;
      gap: 6px;
    }

    &__label {
      font-weight: 500;
      color: var(--color-text-secondary);
    }

    &__value {
      font-weight: 600;
      color: var(--color-text-primary);

      &--money {
        font-variant-numeric: tabular-nums;
      }

      &--positive {
        font-variant-numeric: tabular-nums;
        color: var(--color-success, #16a34a);
      }

      &--negative {
        font-variant-numeric: tabular-nums;
        color: var(--color-danger, #dc2626);
      }
    }

    &__sep {
      color: var(--color-border);
      user-select: none;
    }

    &__hint {
      margin-left: auto;
      font-size: 12px;
      color: var(--color-text-secondary);
      opacity: 0.85;
    }
  }

  .my-today-btn {
    flex-shrink: 0;

    &.is-active {
      box-shadow: 0 0 0 1px var(--color-primary) inset;
    }
  }

  .filters {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;

    .date-picker {
      width: 260px;
    }

    .filter-select {
      width: 140px;
    }

    .filter-select--handler {
      width: 160px;
    }

    .filter-select--payment {
      width: 132px;
    }

    .flex-spacer {
      flex: 1;
    }

    .search-input {
      width: 280px;
    }

    :deep(.el-input__wrapper),
    :deep(.el-select .el-input__wrapper),
    :deep(.el-date-editor .el-input__wrapper) {
      border-radius: var(--radius-md);
      box-shadow: 0 0 0 1px var(--color-border) inset;
      transition:
        box-shadow var(--motion-fast) var(--ease-standard),
        border-color var(--motion-fast) var(--ease-standard);
    }

    :deep(.el-input__wrapper:hover),
    :deep(.el-select .el-input__wrapper:hover),
    :deep(.el-date-editor .el-input__wrapper:hover) {
      box-shadow: 0 0 0 1px #b8b8be inset;
    }

    :deep(.el-input__wrapper.is-focus),
    :deep(.el-select .el-input__wrapper.is-focus),
    :deep(.el-date-editor .el-input__wrapper.is-focus) {
      box-shadow:
        0 0 0 1px var(--color-primary) inset,
        var(--focus-ring);
    }
  }

  .text-gray {
    color: var(--color-text-secondary);
    font-size: var(--font-size-body-sm);
    line-height: var(--line-height-body-sm);
  }

  .pagination {
    margin-top: var(--space-4);
    display: flex;
    justify-content: flex-end;

    :deep(.el-pagination .el-pager .number) {
      border-radius: var(--radius-md);
      transition:
        background-color var(--motion-fast) var(--ease-standard),
        color var(--motion-fast) var(--ease-standard),
        transform var(--motion-fast) var(--ease-standard);
      font-weight: 500;
    }

    :deep(.el-pagination .el-pager .number:hover) {
      color: var(--color-primary);
      background: var(--color-primary-soft);
      transform: translateY(-1px);
    }

    :deep(.el-pagination .el-pager .number.is-active) {
      background: var(--color-primary);
      color: #fff;
    }
  }

  :deep(.el-dropdown) {
    .base-button {
      min-width: 52px;
      justify-content: center;
    }
  }

  :deep(.el-tag) {
    padding: 0 var(--space-3);
    height: 24px;
    line-height: 24px;
    border-radius: 999px;
    font-weight: 500;
  }

  :deep(.el-table) {
    --el-table-header-bg-color: var(--color-surface-muted);
    --el-table-row-hover-bg-color: var(--color-primary-soft);

    th.el-table__cell {
      font-size: var(--font-size-body-sm);
      color: var(--color-text-secondary);
      font-weight: 600;
    }

    td.el-table__cell {
      font-size: 14px;
      color: var(--color-text-primary);
    }

    .el-table__row {
      transition: background-color var(--motion-fast) var(--ease-standard);
    }
  }
}

@media print {
  .el-dialog {
    display: none;
  }
  
  .dialog-footer {
    display: none;
  }
}

:deep(.thermal-dialog) {
  .el-dialog__body {
    display: flex;
    justify-content: center;
    background-color: #f5f5f5;
    padding: 20px;
  }
}

@include mobile {
  .order-list {
    padding: var(--space-3);
    gap: var(--space-3);
  }

  .toolbar-card :deep(.base-card__body) {
    padding: 12px !important;
  }

  .filters.filter-section {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    align-items: stretch;

    .date-picker,
    .filter-select,
    .search-input {
      width: 100% !important;
      min-width: 0;
    }

    :deep(.el-date-editor) {
      width: 100% !important;
    }

    :deep(.el-select .el-input__wrapper),
    :deep(.el-input__wrapper),
    :deep(.el-date-editor .el-input__wrapper) {
      min-height: 32px !important;
    }
  }

  .table-card :deep(.base-card__body) {
    padding: 12px !important;
  }

  .order-summary-bar {
    margin: 0 12px 10px !important;
    padding: 10px 12px !important;
    flex-direction: column;
    align-items: flex-start;

    &__hint {
      margin-left: 0;
    }
  }

  .pagination {
    justify-content: center;
    flex-wrap: wrap;
  }
}

.payment-edit-info {
  font-size: 14px;
  line-height: 1.8;

  &__label {
    color: var(--color-text-secondary);
  }
}
</style>
